import { useRef, useState } from "react";
import { Button, DatePicker, Space } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import type { FilterConfirmProps } from "antd/es/table/interface";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

interface ColumnDateFilterProps<T> {
  dataIndex: keyof T;
}

const formatarDataFiltro = (value: any) => {
  if (!value) return "";

  const parsedDefault = dayjs(value);
  if (parsedDefault.isValid()) {
    return parsedDefault.format("YYYY-MM-DD");
  }

  const parsedBr = dayjs(value, "DD/MM/YYYY", true);
  if (parsedBr.isValid()) {
    return parsedBr.format("YYYY-MM-DD");
  }

  return "";
};

const ColumnDateFilter = <T extends object>({
  dataIndex,
}: ColumnDateFilterProps<T>): ColumnType<T> => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const datePickerRef = useRef<any>(null);

  const handleSearch = (
    confirm: (param?: FilterConfirmProps) => void,
    value?: string
  ) => {
    confirm();
  };

  const handleReset = (
    clearFilters: (() => void) | undefined,
    confirm: (param?: FilterConfirmProps) => void
  ) => {
    clearFilters?.();
    setSelectedDate(null);
    confirm();
  };

  return {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePicker
          ref={datePickerRef}
          format="DD/MM/YYYY"
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setSelectedKeys(date ? [date.format("YYYY-MM-DD")] : []);
          }}
          style={{ marginBottom: 8, display: "block", width: "100%" }}
          placeholder="Selecione a data"
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(confirm, selectedKeys[0] as string | undefined)
            }
            icon={<CalendarOutlined />}
            size="small"
            style={{ width: 100 }}
          >
            Filtrar
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Limpar
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Fechar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const valorRegistro = formatarDataFiltro(record[dataIndex]);
      return valorRegistro === value;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => datePickerRef.current?.focus?.(), 100);
      }
    },
  };
};

export default ColumnDateFilter;
